// ----------------------------------------------------------------------
// <copyright file="Izenda.GaugeValueSettings.js" company="Izenda">
// Copyright (c) 2015 Izenda, Inc.  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and  International
// Copyright Laws. Unauthorized reproduction,  reverse-engineering, and
// distribution of all or any portion of  the code contained in this file is
// strictly prohibited and may  result in severe civil and criminal penalties
// and will be  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES  ARE CONFIDENTIAL AND
// PROPRIETARY TRADE  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
// LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET  CONTROLS AS
// PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED  FILES OR ANY
// PORTION OF ITS CONTENTS SHALL AT NO TIME BE  COPIED, TRANSFERRED, SOLD,
// DISTRIBUTED, OR OTHERWISE MADE  AVAILABLE TO OTHER INDIVIDUALS WITHOUT
// EXPRESS WRITTEN CONSENT  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON  ADDITIONAL
// RESTRICTIONS.
//
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * GaugeValueSettings component
 */

import React, { Component } from 'react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import { isEmpty, get, getOr } from 'lodash/fp';
import connect from 'share/enhancers/Izenda.Connect';
import GaugeContants, {
  SETTING_VALUES,
  THRESHOLD_VALUES,
  THRESHOLD_OPERATORS,
  ContainerName
} from 'logic/data/Izenda.ReportPartGaugeConstants';

import DropDown from 'share/components/controls/Izenda.DropDown';
import FieldContainer from './Izenda.FieldContainer';
import InputByDataType from 'share/components/controls/Izenda.InputByDataType';
import EventsUtils from 'EventUtils';
import classnames from 'classnames';

const notifyChange = EventsUtils.notifyChange;

const GRID_LABEL = 'col-md-3 gauge-label control-label';
const GRID_CTRL = 'col-md-9 gauge-control';

/**
 * Field container: alow user to drag & drop or open pop-up to add Field
 */
const FieldContainerWrapper = observer(props => {
  const value = {
    name: props.containerName,
    elements: isEmpty(props.value) ? [] : [props.value]
  };
  return (
    <FieldContainer
      {...props}
      height={30}
      maximumField={1}
      showPlaceHolder={false}
      resizable={true}
      onDeleteElement={onDelete}
      onAddElement={onAdd}
      container={observable(value)}
    />
  );

  function onAdd(addedField) {
    props.onAdd && props.onAdd.call(null, addedField);
  }

  function onDelete() {
    props.onDelete && props.onDelete.apply(null, arguments);
  }
});

/**
 * SettingValueBy Type
 * type = static: number
 * type = dynamic: field-container
 */
const SettingValueByType = observer(props => {
  const isStatic = props.type === SETTING_VALUES.STATIC.value;
  let valueKey = props.valueKey || (isStatic ? 'value' : 'element');

  valueKey += props.isUpperBound ? 'Upper' : '';

  const value = {
    value: get(valueKey, props.dataSource || {})
  };

  const vals =
    props.name === THRESHOLD_VALUES.LOW.value || props.name === THRESHOLD_VALUES.HIGH.value
      ? {
          action() {
            return props.metricModel.validateThreshold.bind(props.metricModel, props.name);
          }
        }
      : {};

  const onBlur = () => {
    props.validation.validate();
  };

  return isStatic ? (
    <InputByDataType
      type="number"
      isDecimal
      onChange={onChange}
      {...value}
      onBlur={onBlur}
      validations={vals}
      field={props.name}
    />
  ) : (
    <FieldContainerWrapper {...props} onDelete={onDeleteField.bind(this)} onAdd={onAddField.bind(this)} {...value} />
  );

  function onChange(newVal) {
    return props.onChange(newVal.value, props);
  }

  function onDeleteField(newVal) {
    return onChange({ value: null });
  }

  function onAddField(addedField) {
    return onChange({ value: addedField });
  }
});

/**
 * ThresholdSettingItem: name & color & operator & value
 */
const ThresholdSettingItem = observer(props => {
  props = props || {};
  const classes = classnames('threshold-item', 'form-group');
  const thresholdData = props.thresholdData || {};
  const metricModel = props.metricModel;
  const thresholdName = thresholdData.name;

  return (
    <fieldset className={classes}>
      <span className={GRID_LABEL}>{THRESHOLD_VALUES[thresholdName.toUpperCase()].label}</span>
      <div className={GRID_CTRL}>
        <InputByDataType
          type="color"
          value={thresholdData.color}
          onChange={EventsUtils.notifyChange(changeThresholdValues('color'), EventsUtils.INPUT_BY_TYPE)}
        />
        <DropDown
          disabled={isDisabled()}
          options={getOperators()}
          value={thresholdData.operator}
          onChange={EventsUtils.notifyChange(changeThresholdValues('operator'), EventsUtils.DROPDOWN)}
        />
        {renderValue(thresholdData.operator, props)}
      </div>
    </fieldset>
  );

  function isDisabled() {
    switch (thresholdName) {
      case THRESHOLD_VALUES.LOW.value:
      case THRESHOLD_VALUES.HIGH.value:
        return true;
      default:
        return false;
    }
  }

  function getOperators() {
    switch (thresholdName) {
      case THRESHOLD_VALUES.LOW.value:
        return [THRESHOLD_OPERATORS.LESS_THAN];

      case THRESHOLD_VALUES.TARGET.value:
        return [THRESHOLD_OPERATORS.LESS_THAN, THRESHOLD_OPERATORS.BETWEEN];

      case THRESHOLD_VALUES.HIGH.value:
        return [THRESHOLD_OPERATORS.GREAT_THAN];

      default:
        return [];
    }
  }

  function renderValue(operator, props) {
    switch (operator) {
      case THRESHOLD_OPERATORS.LESS_THAN.value:
        return renderSettingValue({ isUpperBound: true });

      case THRESHOLD_OPERATORS.BETWEEN.value:
        return (
          <div className="threshold-range">
            {renderSettingValue({ isUpperBound: false })}
            {renderSettingValue({ isUpperBound: true })}
          </div>
        );

      case THRESHOLD_OPERATORS.GREAT_THAN.value:
        return renderSettingValue({ isUpperBound: false });
    }

    function renderSettingValue(settingInfo) {
      return (
        <SettingValueByType
          {...props}
          {...settingInfo}
          type={props.type}
          containerName={ContainerName.THRESHOLD}
          dataSource={thresholdData}
          onChange={changeThresholdValues(null, settingInfo, true)}
          name={thresholdData.name}
        />
      );
    }
  }

  function changeThresholdValues(keyValue, settingInfo, isSettingValue) {
    return (newVal, otherInfo) => {
      metricModel.setThresholdValue &&
        metricModel.setThresholdValue(
          isSettingValue,
          thresholdData,
          keyValue,
          newVal,
          otherInfo && otherInfo.isUpperBound
        );
    };
  }
});

/**
 * ThresholdSetting: low & target & high
 */
const ThresholdSetting = connect()(props => {
  const localization = props.localization;
  const thresholdSetting = props.thresholdSetting || '';
  const metricModel = props.metricModel;
  const classes = classnames('form-group');
  const thresholdSettingLevels = thresholdSetting.levels;

  return (
    <fieldset disabled={props.disabled}>
      <div className="form-group">
        <span className={GRID_LABEL}>{localization.getText('REPORTDESIGNER_LABEL_THRESHOLD_SETTINGS')}</span>
        <div className={GRID_CTRL}>
          <DropDown
            disabled={props.disabled}
            options={GaugeContants.getOptions('THRESHOLD_SETTINGS')}
            blankOption={true}
            blankOptionText="None"
            value={thresholdSetting.setting}
            onChange={EventsUtils.notifyChange(metricModel.setThresholdSetting.bind(metricModel), EventsUtils.DROPDOWN)}
          />
        </div>
      </div>
      {thresholdSettingLevels.map((levelData, index) => (
        <ThresholdSettingItem
          {...props}
          key={index}
          thresholdData={levelData}
          metricModel={metricModel}
          key={levelData.name}
          type={thresholdSetting.setting}
          itemIndex={index}
        />
      ))}
    </fieldset>
  );

  function changeThesholdSetting(newVal) {
    metricModel.setValueByKey && metricModel.setValueByKey('threshold', newVal);
  }
});

/**
 * SupplementarySetting: settings & value & unit
 */
const SupplementarySetting = connect()(props => {
  const localization = props.localization;
  const valueType = getOr('', 'supplementaryKPI.valueType', props);
  const supplementaryKPI = getOr({}, 'supplementaryKPI', props);
  const metricModel = props.metricModel;

  return (
    <fieldset disabled={props.disabled}>
      <div className="form-group">
        <label className={GRID_LABEL}>{localization.getText('REPORTDESIGNER_LABEL_SUPPLEMENTARY_KPI')}</label>
        <div className={GRID_CTRL}>
          <DropDown
            disabled={props.disabled}
            options={GaugeContants.getOptions('SUPP_SETTINGS')}
            blankOption={true}
            blankOptionText="None"
            value={supplementaryKPI.valueType}
            onChange={EventsUtils.notifyChange(
              metricModel.setSupplementaryKPISetting.bind(metricModel),
              EventsUtils.DROPDOWN
            )}
          />
        </div>
      </div>
      {supplementaryKPI.valueType && (
        <div className="form-group">
          <span className={GRID_LABEL}>{localization.getText('COMMON_LABEL_VALUE')}</span>
          <div className={GRID_CTRL}>
            <SettingValueByType
              {...props}
              type={valueType}
              containerName={ContainerName.SUP_KPI}
              dataSource={supplementaryKPI}
              onChange={onChangeValue}
            />
          </div>
        </div>
      )}

      {supplementaryKPI.valueType && (
        <div className="form-group">
          <span className={GRID_LABEL}>{localization.getText('REPORTDESIGNER_LABEL_UNIT_LABEL')}</span>
          <div className={GRID_CTRL}>
            <InputByDataType
              type="text"
              value={supplementaryKPI.unitLabel}
              onChange={EventsUtils.notifyChange(
                changeMetricValue(metricModel, 'supplementaryKPI.unitLabel'),
                EventsUtils.INPUT_BY_TYPE
              )}
            />
          </div>
        </div>
      )}
    </fieldset>
  );

  function onChangeValue(newVal) {
    metricModel.setSupKpiValue && metricModel.setSupKpiValue(newVal);
  }
});

function changeMetricValue(metricModel, changedKeyPath) {
  return function changeValue(newVal) {
    metricModel.setDataByKey && metricModel.setDataByKey.call(metricModel, changedKeyPath, newVal);
  };
}

export default {
  FieldContainer: FieldContainerWrapper,
  SupplementarySetting,
  ThresholdSetting: ThresholdSetting
};



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/FieldContainers/Izenda.GaugeValueSettings.js